"use client";

import { Fragment, JSX, useRef } from "react";
import { fmtNum } from "@/lib/format";
import { useViewerLanguage } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { useHoverCapable } from "@/lib/hooks/use-hover-capable";
import { useHoverSelect } from "@/lib/hooks/use-hover-select";
import { LegendScale } from "@/components/charts/legend";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import { NodeTooltip } from "@/components/charts/tooltip/node-tooltip";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import { MODULE_LABEL_WRAP } from "./module-label-budget";
import { importsTokenScope } from "./imports-token-scope";
import { accentMarkColor } from "./module-accent";

export type SkillLevelMatrixData = {
  rowLabels: string[];
  colLabels: string[];
  levels: number[][];
  maxLevel?: number;
};

const DEFAULT_DATA: SkillLevelMatrixData = {
  rowLabels: ["TypeScript", "Python", "Go", "Rust", "SQL", "AWS", "K8s", "Docker"],
  colLabels: ["Alex", "Ben", "Cara", "Dan", "Eli", "Fay"],
  levels: [
    [4, 3, 4, 5, 2, 3],
    [5, 4, 2, 3, 4, 5],
    [3, 2, 1, 4, 0, 2],
    [1, 0, 0, 2, 0, 1],
    [4, 5, 3, 4, 5, 4],
    [3, 4, 5, 3, 4, 3],
    [2, 3, 4, 4, 3, 2],
    [4, 4, 5, 4, 3, 4],
  ],
  maxLevel: 5,
};

type HoveredCell = { row: number; col: number };

export default function ModuleSkillLevelMatrix({
  data = DEFAULT_DATA,
  grayscale,
  accent,
  className,
}: {
  data?: SkillLevelMatrixData;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const { t, intlLocale } = useViewerLanguage();
  const rowLabels = data.rowLabels ?? [];
  const colLabels = data.colLabels ?? [];
  const levels = data.levels ?? [];
  const maxLevel = data.maxLevel ?? 5;

  const cell = useHoverSelect<HoveredCell>({
    eq: (a, b) => a.row === b.row && a.col === b.col,
  });
  const active = cell.effective;
  const anchorRefs = useRef<Record<string, SVGGElement | null>>({});
  const hasHover = useHoverCapable();

  const cellW = 84;
  const cellH = 44;
  const leftPad = 130;
  const topPad = 50;
  const rightPad = 80;
  const bottomPad = 50;
  const W = leftPad + colLabels.length * cellW + rightPad;
  const H = topPad + rowLabels.length * cellH + bottomPad;

  const avgRow = (row: number[]): number =>
    row.length ? row.reduce((a, v) => a + v, 0) / row.length : 0;
  const avgCol = (colIdx: number): number => {
    const vals = levels.map((row) => row[colIdx] ?? 0);
    return vals.length ? vals.reduce((a, v) => a + v, 0) / vals.length : 0;
  };

  const activeLevel =
    active !== null ? levels[active.row]?.[active.col] : undefined;

  return (
    <div className={cn("flex w-full flex-col gap-3", className)} style={importsTokenScope(grayscale, accent)}>
      <div className="min-h-[1.25rem] text-xs text-[var(--muted-foreground)]">
        {active !== null && activeLevel !== undefined ? (
          <>
            <span className="mr-2 text-[10px] text-[var(--subtle)]">
              {t("Detail")}
            </span>
            <span
              className={cn(
                "font-medium text-[var(--foreground)]",
                MODULE_LABEL_WRAP.label,
              )}
            >
              {stripInlineMarkdown(rowLabels[active.row])}
            </span>
            {" · "}
            <span className={MODULE_LABEL_WRAP.label}>
              {stripInlineMarkdown(colLabels[active.col])}
            </span>
            {" · "}
            <span className="font-medium tabular-nums text-[var(--foreground)]">
              {activeLevel}/{maxLevel}
            </span>
          </>
        ) : (
          <span className="text-[var(--subtle)]">
            {t("Select a cell for level detail")}
          </span>
        )}
      </div>

      <SwipeScroll className="relative w-full">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="h-auto w-full"
          style={{ overflow: "visible", minWidth: "640px" }}
        >
          {colLabels.map((p, i) => {
            const x = leftPad + i * cellW + cellW / 2;
            const isHl = active !== null && active.col === i;
            return (
              <text
                key={`col-${i}`}
                x={x}
                y={topPad - 14}
                textAnchor="middle"
                fontSize="12"
                fontWeight="500"
                fill={isHl ? "var(--foreground)" : "var(--muted-foreground)"}
                style={{ transition: "fill 0.15s" }}
              >
                {stripInlineMarkdown(p)}
              </text>
            );
          })}
          <text
            x={leftPad + colLabels.length * cellW + rightPad / 2}
            y={topPad - 14}
            textAnchor="middle"
            fontSize="11"
            fill="var(--subtle)"
          >
            {t("Avg")}
          </text>

          {rowLabels.map((skill, r) => {
            const y = topPad + r * cellH;
            const isRowHl = active !== null && active.row === r;

            return (
              <Fragment key={r}>
                <text
                  x={leftPad - 14}
                  y={y + cellH / 2 + 4}
                  textAnchor="end"
                  fontSize="12"
                  fontWeight={isRowHl ? 600 : 500}
                  fill={isRowHl ? "var(--foreground)" : "var(--muted-foreground)"}
                  style={{ transition: "fill 0.15s" }}
                >
                  {stripInlineMarkdown(skill)}
                </text>

                {colLabels.map((_, c) => {
                  const x = leftPad + c * cellW;
                  const level = levels[r]?.[c] ?? 0;
                  const isHovered =
                    active !== null && active.row === r && active.col === c;
                  const isHl =
                    active !== null &&
                    (active.row === r || active.col === c);
                  const dim = active !== null && !isHl;

                  return (
                    <g
                      key={c}
                      ref={(el) => {
                        anchorRefs.current[`${r}:${c}`] = el;
                      }}
                      data-module-type="skill-level-matrix"
                      data-part-index={`${r}:${c}`}
                      data-part-label={`${skill} × ${colLabels[c] ?? ""}`}
                      onMouseEnter={
                        hasHover
                          ? () => cell.setHovered({ row: r, col: c })
                          : undefined
                      }
                      onMouseLeave={
                        hasHover ? () => cell.setHovered(null) : undefined
                      }
                      onClick={(e) =>
                        cell.select(
                          { row: r, col: c },
                          {
                            kind: "cell",
                            id: `${r}:${c}`,
                            index: r * colLabels.length + c,
                            element: e.currentTarget,
                          },
                        )
                      }
                      style={{ cursor: "pointer", transition: "opacity 0.15s" }}
                      opacity={dim ? 0.3 : 1}
                    >
                      {isHovered && (
                        <rect
                          x={x + 6}
                          y={y + 6}
                          width={cellW - 12}
                          height={cellH - 12}
                          rx="4"
                          fill="none"
                          stroke="var(--border)"
                          strokeWidth="1"
                        />
                      )}
                      {isHl && !isHovered && (
                        <rect
                          x={x + 6}
                          y={y + 6}
                          width={cellW - 12}
                          height={cellH - 12}
                          rx="4"
                          fill="var(--foreground)"
                          fillOpacity="0.02"
                        />
                      )}
                      {Array.from({ length: maxLevel }).map((_, i) => {
                        const spacing = 9;
                        const dotX =
                          x + cellW / 2 -
                          ((maxLevel - 1) * spacing) / 2 +
                          i * spacing;
                        const dotY = y + cellH / 2;
                        const filled = i < level;
                        return (
                          <circle
                            key={i}
                            cx={dotX}
                            cy={dotY}
                            r="2.75"
                            fill={filled ? "var(--module-accent)" : "transparent"}
                            stroke={filled ? "none" : "var(--chart-4)"}
                            strokeWidth="1"
                          />
                        );
                      })}
                    </g>
                  );
                })}

                <text
                  x={leftPad + colLabels.length * cellW + rightPad / 2}
                  y={y + cellH / 2 + 4}
                  textAnchor="middle"
                  fontSize="13"
                  fontWeight="600"
                  fill={isRowHl ? "var(--foreground)" : "var(--muted-foreground)"}
                  style={{
                    fontVariantNumeric: "tabular-nums",
                    transition: "fill 0.15s",
                  }}
                >
                  {fmtNum(avgRow(levels[r] ?? []), 1, intlLocale)}
                </text>
              </Fragment>
            );
          })}

          <text
            x={leftPad - 14}
            y={topPad + rowLabels.length * cellH + 26}
            textAnchor="end"
            fontSize="11"
            fill="var(--subtle)"
          >
            {t("Avg")}
          </text>
          {colLabels.map((_, c) => {
            const x = leftPad + c * cellW + cellW / 2;
            const y = topPad + rowLabels.length * cellH + 26;
            const isColHl = active !== null && active.col === c;
            return (
              <text
                key={c}
                x={x}
                y={y}
                textAnchor="middle"
                fontSize="13"
                fontWeight="600"
                fill={isColHl ? "var(--foreground)" : "var(--muted-foreground)"}
                style={{
                  fontVariantNumeric: "tabular-nums",
                  transition: "fill 0.15s",
                }}
              >
                {fmtNum(avgCol(c), 1, intlLocale)}
              </text>
            );
          })}
        </svg>
      </SwipeScroll>

      <NodeTooltip
        visible={active !== null}
        anchor={
          active ? anchorRefs.current[`${active.row}:${active.col}`] ?? null : null
        }
        contentKey={active ? `${active.row}:${active.col}` : undefined}
        title={
          active
            ? `${stripInlineMarkdown(rowLabels[active.row])} · ${stripInlineMarkdown(colLabels[active.col])}`
            : undefined
        }
        rows={
          active && activeLevel !== undefined
            ? [
                {
                  color: accentMarkColor(grayscale, accent),
                  label: t("Level"),
                  value: `${activeLevel} / ${maxLevel}`,
                },
              ]
            : []
        }
      />

      <SwipeScroll>
        <LegendScale
          caption="Level"
          color="var(--module-accent)"
          steps={Array.from({ length: maxLevel }, (_, i) => ({
            label: String(i + 1),
          }))}
        />
      </SwipeScroll>
    </div>
  );
}
